import InfiniteScroll from "react-infinite-scroll-component";
import { AccessLevel } from "@/api/williams/users";
import { Contractor } from "@/api/williams/contractors";
import { downloadCertificate } from "@/api/williams/certificates";
import { ExpandableCard, PinButton, useSelectedItems } from "../../components";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { faSquarePlus } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFullPath, routes } from "../../routes";
import { getStandardName } from "../lookup/standard";
import { highlight } from "@/util";
import { Link } from "react-router-dom";
import { TestFilters, TestListView } from "@/api/williams/tests";
import { usePermissions } from "../../users/hooks";
import { usePinTest, useStandardTestList } from "../hooks";
import { useState } from "react";

export type StandardTestsProps = {
  filters: Partial<TestFilters>;
  contractor?: Contractor;
  expanded?: boolean;
  allowExpand?: boolean;
};

export function StandardTests({ filters, contractor, ...props }: StandardTestsProps) {
  const tests = useStandardTestList(filters);
  const permissions = usePermissions();

  const data = tests.data?.pages.flatMap((page) => page.results) ?? [];
  const total = tests.data?.pages[0]?.total ?? 0;
  const readOnly = permissions.data?.accessLevel === AccessLevel.GeneralUser;

  return (
    <ExpandableCard
      caption={`Standard Tests (${total})`}
      expanded={props.expanded}
      allowExpand={props.allowExpand}
      headerButtons={[
        <Link to={getFullPath(routes.standardTests.create)} state={{ contractor }} hidden={readOnly}>
          <FontAwesomeIcon icon={faSquarePlus} size="sm" title="Submit Request" />
        </Link>,
      ]}
    >
      <InfiniteScroll
        dataLength={data.length}
        next={tests.fetchNextPage}
        hasMore={Boolean(tests.hasNextPage)}
        loader={<p>Loading...</p>}
      >
        {tests.isLoading ? (
          <p>Loading...</p>
        ) : tests.isError ? (
          <p>An unknown error occurred.</p>
        ) : tests.isSuccess && data.length > 0 ? (
          <StandardTestsTable data={data} search={filters.searchString} />
        ) : filters.searchString ? (
          <p>Your search returned no results.</p>
        ) : (
          <p>No standard tests found.</p>
        )}
      </InfiniteScroll>
    </ExpandableCard>
  );
}

export function StandardTestsTable({ data, search }: { data: TestListView[]; search?: string }) {
  const allowSelection = useSelectedItems<TestListView>();

  return (
    <table className="table table-sm table-borderless mb-0">
      <thead>
        <tr>
          {allowSelection && <th className="col-auto" />}
          <th className="col col-md-6">Test Number</th>
          <th className="col-6 d-none d-md-table-cell">Standard</th>
          <th className="col-auto" />
        </tr>
      </thead>
      <tbody>
        {data.map((test) => (
          <StandardTestRow key={test.id} test={test} search={search} />
        ))}
      </tbody>
    </table>
  );
}

function StandardTestRow({ test, search }: { test: TestListView; search?: string }) {
  const [expanded, setExpanded] = useState<boolean>(false);

  const pin = usePinTest();
  const selected = useSelectedItems<TestListView>();

  function handleSelectTest(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.checked) {
      selected.add(test);
    } else {
      selected.remove((x) => x.id === test.id);
    }
  }

  return (
    <>
      <tr key={`${test.id}_${test.pinned}`}>
        {selected && (
          <td className="ps-0" style={{ width: 0 }}>
            <input
              type="checkbox"
              className="form-check-input"
              checked={selected.items.some((x) => x.id === test.id)}
              onChange={handleSelectTest}
            />
          </td>
        )}
        <td>
          <a className="link link-primary" onClick={() => downloadCertificate(test.id)}>
            {highlight(test.number, search)}
          </a>
        </td>
        <td className="d-none d-md-table-cell">{getStandardName(test.standard)}</td>
        <td className="text-end text-nowrap">
          <FontAwesomeIcon
            role="button"
            icon={expanded ? faChevronUp : faChevronDown}
            size="sm"
            className="me-1"
            title="Details"
            onClick={() => setExpanded((prev) => !prev)}
            fixedWidth={true}
          />
          <PinButton pinned={test.pinned} onChange={() => pin.mutate(test.id)} />
        </td>
      </tr>
      {expanded && (
        <tr>
          {selected && <td />}
          <td className="d-none d-md-table-cell" colSpan={3}>
            <ExpandedDetails test={test} search={search} />
          </td>
          <td className="d-table-cell d-md-none" colSpan={2}>
            <ExpandedDetails test={test} search={search} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetails({ test, search }: { test: TestListView; search?: string }) {
  return (
    <div className="ms-1">
      <div className="bg-light p-1">Test Details</div>
      <div className="p-1">
        <dl>
          <dt className="d-block d-md-none">Standard</dt>
          <dd className="d-block d-md-none">{getStandardName(test.standard)}</dd>
        </dl>
      </div>
    </div>
  );
}
